import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { UserGroupIcon, AcademicCapIcon, PlusIcon, XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const StudentCourseManagement = () => {
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentEnrollments, setStudentEnrollments] = useState([]);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const loadInitialData = async () => {
      setInitialLoading(true);
      await Promise.all([fetchStudents(), fetchCourses()]);
      setInitialLoading(false);
    };
    loadInitialData();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await api.get('/users');
      const studentUsers = Array.isArray(response.data) 
        ? response.data.filter(user => user.role === 'student')
        : [];
      setStudents(studentUsers);
    } catch (err) {
      console.error('Failed to fetch students:', err);
      setError('Failed to load students');
      setStudents([]); // Ensure students is always an array
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await api.get('/courses', { params: { limit: 1000 } });
      console.log('Courses API response:', response.data);
      // Handle both array response and paginated response {data: [...], pagination: {...}}
      let coursesData;
      if (Array.isArray(response.data)) {
        coursesData = response.data;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        coursesData = response.data.data;
      } else {
        coursesData = [];
      }
      console.log('Courses after processing:', coursesData);
      setCourses(coursesData);
    } catch (err) {
      console.error('Failed to fetch courses:', err);
      setError('Failed to load courses');
      setCourses([]); // Ensure courses is always an array
    }
  };

  const handleStudentSelect = async (student) => {
    setSelectedStudent(student);
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      // Fetch student's current enrollments using admin endpoint
      const response = await api.get(`/enrollments/student/${student.id}`);
      const enrollments = Array.isArray(response.data) ? response.data : [];
      setStudentEnrollments(enrollments);

      // Calculate available courses (courses not yet enrolled)
      const enrolledCourseIds = enrollments.map(e => e.courseId);
      console.log('Enrolled course IDs:', enrolledCourseIds);
      console.log('All courses:', courses);
      
      const available = Array.isArray(courses) 
        ? courses.filter(course => !enrolledCourseIds.includes(course.id))
        : [];
      
      console.log('Available courses:', available);
      setAvailableCourses(available);
    } catch (err) {
      console.error('Failed to fetch student enrollments:', err);
      // If no enrollment data available, show all courses as available
      setStudentEnrollments([]);
      const allCourses = Array.isArray(courses) ? courses : [];
      console.log('Error case - setting all courses as available:', allCourses);
      setAvailableCourses(allCourses);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCourse = async (courseId) => {
    if (!selectedStudent) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Use admin endpoint to enroll student
      await api.post('/enrollments/admin/enroll', {
        userId: selectedStudent.id,
        courseId: courseId
      });

      setSuccess('Student successfully enrolled in course!');
      
      // Refresh the student's enrollments
      handleStudentSelect(selectedStudent);
    } catch (err) {
      console.error('Failed to enroll student:', err);
      setError(err.response?.data?.error || 'Failed to enroll student in course');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCourse = async (courseId) => {
    if (!selectedStudent) return;

    if (!window.confirm('Are you sure you want to remove this student from the course?')) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Use admin endpoint to unenroll student
      await api.delete(`/enrollments/admin/unenroll/${courseId}`, {
        data: { userId: selectedStudent.id }
      });

      setSuccess('Student successfully removed from course!');
      
      // Refresh the student's enrollments
      handleStudentSelect(selectedStudent);
    } catch (err) {
      console.error('Failed to unenroll student:', err);
      setError(err.response?.data?.error || 'Failed to remove student from course');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <div className="flex items-center mb-6">
        <UserGroupIcon className="h-8 w-8 text-blue-600 mr-3" />
        <h2 className="text-2xl font-bold text-gray-900">Student Course Management</h2>
      </div>

      {initialLoading && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-center">
          Loading students and courses...
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {success}
        </div>
      )}

      {!initialLoading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Student Selection */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <UserGroupIcon className="h-5 w-5 text-gray-600 mr-2" />
            Select Student
          </h3>
          
          {/* Search Input */}
          <div className="mb-4 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search students by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
            {students.filter(student => {
              const query = searchQuery.toLowerCase();
              return (
                student.name.toLowerCase().includes(query) ||
                student.email.toLowerCase().includes(query) ||
                (student.studentType && student.studentType.toLowerCase().includes(query))
              );
            }).length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                {searchQuery ? 'No students match your search' : 'No students found'}
              </div>
            ) : (
              students.filter(student => {
                const query = searchQuery.toLowerCase();
                return (
                  student.name.toLowerCase().includes(query) ||
                  student.email.toLowerCase().includes(query) ||
                  (student.studentType && student.studentType.toLowerCase().includes(query))
                );
              }).map(student => (
                <div
                  key={student.id}
                  onClick={() => handleStudentSelect(student)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleStudentSelect(student);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  className={`p-4 cursor-pointer border-b border-gray-100 hover:bg-blue-50 transition-colors ${
                    selectedStudent?.id === student.id ? 'bg-blue-100 border-l-4 border-l-blue-500' : ''
                  }`}
                >
                  <p className="font-medium text-gray-900">{student.name}</p>
                  <p className="text-sm text-gray-600">{student.email}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Level: {student.studentType?.toUpperCase() || 'N/A'}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Course Management */}
        <div>
          {selectedStudent ? (
            <>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Managing: <span className="text-blue-600">{selectedStudent.name}</span>
              </h3>

              {/* Current Enrollments */}
              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-700 mb-3 flex items-center">
                  <AcademicCapIcon className="h-5 w-5 text-green-600 mr-2" />
                  Enrolled Courses ({Array.isArray(studentEnrollments) ? studentEnrollments.length : 0})
                </h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {!Array.isArray(studentEnrollments) || studentEnrollments.length === 0 ? (
                    <p className="text-gray-500 text-sm p-3 bg-gray-50 rounded-lg">
                      {!Array.isArray(studentEnrollments) ? 'Loading...' : 'Not enrolled in any courses yet'}
                    </p>
                  ) : (
                    studentEnrollments.map(enrollment => {
                      // Use enrollment.course if available, otherwise try to find in courses array
                      const course = enrollment.course || courses.find(c => c.id === enrollment.courseId);
                      return (
                        <div
                          key={enrollment.id}
                          className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                        >
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">
                              {course?.title || enrollment.courseName || 'Unknown Course'}
                            </p>
                            {course?.code && (
                              <p className="text-xs text-gray-600">Code: {course.code}</p>
                            )}
                          </div>
                          <button
                            onClick={() => handleRemoveCourse(enrollment.courseId)}
                            disabled={loading}
                            className="ml-2 p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-50"
                            title="Remove from course"
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Available Courses */}
              <div>
                <h4 className="text-md font-medium text-gray-700 mb-3 flex items-center">
                  <PlusIcon className="h-5 w-5 text-blue-600 mr-2" />
                  Available Courses ({Array.isArray(availableCourses) ? availableCourses.length : 0})
                </h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {!Array.isArray(availableCourses) || availableCourses.length === 0 ? (
                    <p className="text-gray-500 text-sm p-3 bg-gray-50 rounded-lg">
                      {!Array.isArray(availableCourses) ? 'Loading...' : 'Student is enrolled in all available courses'}
                    </p>
                  ) : (
                    availableCourses.map(course => (
                      <div
                        key={course.id}
                        className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{course.title}</p>
                          {course.code && (
                            <p className="text-xs text-gray-600">Code: {course.code}</p>
                          )}
                          {course.description && (
                            <p className="text-xs text-gray-500 mt-1">{course.description}</p>
                          )}
                        </div>
                        <button
                          onClick={() => handleAddCourse(course.id)}
                          disabled={loading}
                          className="ml-2 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Add to course"
                        >
                          <PlusIcon className="h-5 w-5" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-500">
                <UserGroupIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p>Select a student to manage their courses</p>
              </div>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
};

export default StudentCourseManagement;
